import { createContext, useContext, ReactNode, useEffect, useState, useCallback } from "react";
import { useApp } from "./AppContext";

export interface SecretContextType {
  hasUntickedDoNotUntick: boolean;
  hasClickedGPU: boolean;
  fetchSecretState: () => void;
  untickedDoNotUntick: () => void;
  clickGPU: () => void;
}

const SecretContext = createContext<SecretContextType | undefined>(undefined);

export function useSecret() {
  const context = useContext(SecretContext);
  if (!context) {
    throw new Error("useSecret must be used within a SecretProvider");
  }
  return context;
}

export default function SecretProvider({ children }: { children: ReactNode }) {
  const { sendMessage, onMessage, sessionReady, lastAuthTime } = useApp();

  const [hasUntickedDoNotUntick, setHasUntickedDoNotUntick] = useState(false);
  const [hasClickedGPU, setHasClickedGPU] = useState(false);

  // ===== DATA FETCHING =====
  const fetchSecretState = useCallback(() => {
    if (!sessionReady) {
      console.warn("[Secret] cannot fetch: session not ready");
      return;
    }

    try {
      sendMessage("secret", "get_secret_state", {});
    } catch (err) {
      console.error("[Secret] failed to fetch:", err);
    }
  }, [sessionReady, sendMessage]);

  // ===== ACTIONS =====
  const untickedDoNotUntick = useCallback(() => {
    if (!sessionReady) {
      console.warn("[Secret] cannot untick: session not ready");
      return;
    }

    try {
      sendMessage("secret", "do_not_untick", { untickedAt: Date.now() });
    } catch (err) {
      console.error("[Secret] failed to untick:", err);
    }
  }, [sessionReady, sendMessage]);

  const clickGPU = useCallback(() => {
    if (!sessionReady) {
      console.warn("[Secret] cannot click gpu: session not ready");
      return;
    }

    try {
      sendMessage("secret", "click_gpu", { clickedAt: Date.now() });
    } catch (err) {
      console.error("[Secret] failed to click gpu:", err);
    }
  }, [sessionReady, sendMessage]);

  // ===== MESSAGE HANDLERS =====
  const handleSecretState = useCallback((message: any) => {
    if (message.service !== "secret") return;

    if (message.action === "secret_state") {
      const toggled = message.payload?.hasUntickedDoNotUntick || false;
      setHasUntickedDoNotUntick(toggled);
      // do not expose or set `hasClickedGPU` from secret_state to avoid indicating the secret
      return;
    }

    if (message.action === "quest_list") {
      // quest list was sent after untick or gpu click - update state for only completed quests
      const quests = message.payload?.quests || [];
      const completedFind3 = quests.find(
        (q: any) => q.id === "find_secret_3" && q.status === "completed",
      );
      const completedFind4 = quests.find(
        (q: any) => q.id === "find_secret_4" && q.status === "completed",
      );

      if (completedFind3) setHasUntickedDoNotUntick(true);
      if (completedFind4) setHasClickedGPU(true);
      return;
    }
  }, []);

  // ===== SUBSCRIPTIONS =====
  useEffect(() => {
    const unsub1 = onMessage("secret_state", handleSecretState);
    const unsub2 = onMessage("quest_list", handleSecretState);

    return () => {
      unsub1();
      unsub2();
    };
  }, [onMessage, handleSecretState]);

  // ===== AUTO-FETCH ON SESSION READY OR RECONNECT =====
  useEffect(() => {
    if (sessionReady) {
      fetchSecretState();
    }
  }, [sessionReady, lastAuthTime, fetchSecretState]);

  const value: SecretContextType = {
    hasUntickedDoNotUntick,
    hasClickedGPU,
    fetchSecretState,
    untickedDoNotUntick,
    clickGPU,
  };

  return <SecretContext.Provider value={value}>{children}</SecretContext.Provider>;
}
